import React, { useEffect, useState } from "react";
import { Input, Typography } from "antd";
import { ColumnConfigModal } from "@/components/common/ColumnConfigModal";
import NumberInput from "@/components/common/NumberInput";

const { Text } = Typography;

export interface DynamicColumnEntry {
  rowId: string;
  label: string;
  options: string[];
  isDynamic: boolean;
  // True when the row has Unit Conversion on AND its Table Column name still
  // contains an "xxx"-style placeholder (3+ consecutive x's) to replace.
  hasUnitConversion: boolean;
  // Previously-applied conversion value, if any — prefills the number input
  // so reopening this modal doesn't reset it back to 0.
  unitConversionValue: number;
}

export interface DynamicColumnUpdate {
  rowId: string;
  options: string[];
  unitConversionValue?: number;
}

interface DynamicColumnsConfigModalProps {
  open: boolean;
  columns: DynamicColumnEntry[];
  onCancel: () => void;
  onSave: (updates: DynamicColumnUpdate[]) => void;
}

const toTempValues = (columns: DynamicColumnEntry[]): Record<string, string> =>
  Object.fromEntries(columns.map((col) => [col.rowId, col.options.join(", ")]));

const toTempConversionValues = (columns: DynamicColumnEntry[]): Record<string, number> =>
  Object.fromEntries(columns.filter((col) => col.hasUnitConversion).map((col) => [col.rowId, col.unitConversionValue]));

const DynamicColumnsConfigModal: React.FC<DynamicColumnsConfigModalProps> = ({
  open,
  columns,
  onCancel,
  onSave,
}) => {
  const [tempValues, setTempValues] = useState<Record<string, string>>({});
  const [tempConversionValues, setTempConversionValues] = useState<Record<string, number>>({});

  useEffect(() => {
    if (open) {
      setTempValues(toTempValues(columns));
      setTempConversionValues(toTempConversionValues(columns));
    }
  }, [open, columns]);

  const handleApply = () => {
    onSave(
      columns.map((col) => ({
        rowId: col.rowId,
        options: (tempValues[col.rowId] ?? "")
          .split(",")
          .map((header) => header.trim())
          .filter(Boolean),
        unitConversionValue: col.hasUnitConversion ? tempConversionValues[col.rowId] : undefined,
      })),
    );
  };

  const handleClose = () => {
    setTempValues(toTempValues(columns));
    setTempConversionValues(toTempConversionValues(columns));
    onCancel();
  };

  return (
    <ColumnConfigModal title="Configure Headers" open={open} onClose={handleClose} onApply={handleApply} width={500}>
      <div className="grid grid-cols-1 gap-3">
        {columns.map((col) => (
          <div key={col.rowId}>
            <Text className="text-xs mb-1 block">{col.label}</Text>
            {col.isDynamic && !col.hasUnitConversion && (
              <Input
                value={tempValues[col.rowId] ?? ""}
                onChange={(e) => setTempValues((prev) => ({ ...prev, [col.rowId]: e.target.value }))}
              />
            )}
            {col.hasUnitConversion && (
              <NumberInput
                // Stored value is millimetres (matches Cut & Fill's Configure
                // Headers convention), but entry is in metres — e.g. typing
                // "0.888" here stores/substitutes "888" into the placeholder.
                value={(() => {
                  const raw = tempConversionValues[col.rowId] ?? 0;
                  return raw >= 1 ? raw / 1000 : raw;
                })()}
                onChange={(metresValue) => {
                  const mmValue = metresValue ? Math.round(metresValue * 1000) : 0;
                  setTempConversionValues((prev) => ({ ...prev, [col.rowId]: mmValue }));
                }}
                positiveOnly
                decimalPlaces={3}
                zeroIntegerOnly
                placeholder="Conversion value"
              />
            )}
          </div>
        ))}
      </div>
    </ColumnConfigModal>
  );
};

export default DynamicColumnsConfigModal;
